/**
 * StudioConfigSections — the compact accordion form both bake-off prototypes
 * share (Canvas inspector rail / Copilot config rail). Binds straight to
 * MeetingConfigContext, so it IS the same form state as the legacy page —
 * three accordions with completeness badges (the same "what's missing" signal
 * Prototype B sends the model as pageContext).
 *
 * PROTOTYPE (bake-off): styling is deliberately lean; the winning shell gets
 * the design-system pass.
 */
import { useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import TimePicker from '../common/TimePicker';
import DatePicker from '../common/DatePicker';
import { Select, DateField, inputSmClass } from '../common/FormControls';
import { useMeetingConfig } from '../../context/MeetingConfigContext';

const MEETING_TYPES = [
  ['board', 'Board Meeting'], ['agm', 'AGM'], ['egm', 'EGM'], ['committee', 'Committee'],
];
const MODES = ['Physical — Registered Office', 'Video Conference', 'Hybrid'];

// Radius scale is deliberate: overlay frame 16 (--r-xl) → section cards 12
// (rounded-xl) → controls 8 (rounded-lg). Controls are the design-system
// compact set (inputSmClass + Select/DateField/TimePicker), not raw elements.
const inputCls = inputSmClass;
const labelCls = 'block text-[9.5px] uppercase tracking-[0.5px] font-semibold text-[#9a9080] mb-1';

// A field flashes teal for ~2s after the copilot patched it ("AI touched this").
// Inline styles ONLY while flashing — a permanent inline border/shadow would
// override the shared control's own hover/focus chrome.
const borderFor = (flashKeys, key) => (
  flashKeys && flashKeys.has(key)
    ? {
      borderColor: '#145f5f',
      boxShadow: '0 0 0 2px rgba(20,95,95,0.25)',
      transition: 'box-shadow 0.4s ease, border-color 0.4s ease',
    }
    : undefined
);

// Shared Select/DateField own their chrome, so the flash rides on a wrapper
// ring instead of the control's own border.
const flashRing = (flashKeys, key) => ({
  borderRadius: 8,
  boxShadow: flashKeys && flashKeys.has(key) ? '0 0 0 2px rgba(20,95,95,0.35)' : 'none',
  transition: 'box-shadow 0.4s ease',
});

function Section({ title, missing, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#e2d9c8' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
      >
        <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#08111f]">{title}</span>
        <span className="flex items-center gap-2">
          {missing > 0 && (
            <span className="text-[9.5px] font-semibold px-1.5 py-[2px] rounded-full" style={{ background: 'rgba(176,137,58,0.12)', color: '#8a6a1f' }}>
              {missing} missing
            </span>
          )}
          <FiChevronDown size={14} className={`text-[#9a9080] transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </div>
  );
}

export default function StudioConfigSections({ flashKeys }) {
  const {
    config, updateConfig,
    agendaChecked, setAgendaChecked,
    customItems, setCustomItems,
    directorsChecked, setDirectorsChecked,
    absentDirectors, setAbsentDirectors,
    invitees, setInvitees,
    getPresets, companyContext,
  } = useMeetingConfig();

  const [newAgenda, setNewAgenda] = useState('');

  const presets = getPresets();
  const directors = companyContext.directors || [];

  const meetingMissing = ['date', 'startTime', 'endTime'].filter((k) => !config[k]).length + (config.meetingNo || config.meetingNoCustom ? 0 : 1);
  const peopleMissing = ['chairperson'].filter((k) => !config[k]).length;

  const field = (key, label, type = 'text', placeholder = '') => (
    <div>
      <label className={labelCls}>{label}</label>
      {type === 'date' ? (
        // Custom picker, not the native control — same one the rest of the app
        // uses, and it keeps the ISO contract update_page_config writes.
        <div style={flashRing(flashKeys, key)}>
          <DateField size="sm" value={config[key] || ''} onChange={(v) => updateConfig(key, v)} placeholder={placeholder || label} />
        </div>
      ) : (
        <input
          type={type}
          className={inputCls}
          style={borderFor(flashKeys, key, !!config[key])}
          value={config[key] || ''}
          placeholder={placeholder}
          onChange={(e) => updateConfig(key, e.target.value)}
        />
      )}
    </div>
  );

  // Dates use the same type-to-fill treatment as the times above: the native
  // date input's segmented cells reject a typed or pasted "23/07/2026" and
  // force the calendar. Same ISO "YYYY-MM-DD" contract, so config is unchanged.
  const dateField = (key, label) => (
    <div>
      <label className={labelCls}>{label}</label>
      <div style={borderFor(flashKeys, key, !!config[key])} className="rounded-lg">
        <DatePicker
          value={config[key] || ''}
          onChange={(v) => updateConfig(key, v)}
          ariaLabel={label}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-2.5">
      <Section title="Meeting" missing={meetingMissing}>
        <div>
          <label className={labelCls}>Meeting type</label>
          <div style={flashRing(flashKeys, 'meetingType')}>
            <Select
              size="sm"
              value={config.meetingType}
              onChange={(v) => updateConfig('meetingType', v)}
              options={MEETING_TYPES.map(([v, l]) => ({ value: v, label: l }))}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls}>Meeting no.</label>
            <input
              className={inputCls}
              style={borderFor(flashKeys, 'meetingNumber', true)}
              value={config.meetingNo === 'custom' ? (config.meetingNoCustom || '') : (config.meetingNo || '')}
              placeholder="e.g. 5th"
              onChange={(e) => { updateConfig('meetingNo', 'custom'); updateConfig('meetingNoCustom', e.target.value); }}
            />
          </div>
          <div>
            <label className={labelCls}>Financial year</label>
            <input className={inputCls} style={{ borderColor: '#e2d9c8', background: '#faf8f2' }} value={config.fy || ''} readOnly title="Derived from the meeting date" />
          </div>
        </div>
        {dateField('date', 'Date of meeting')}
        {/* Same TimePicker as the Classic view — 12h type-to-fill, 24h "HH:MM"
            data contract (the ValRef-hardened one), not the native input. */}
        <div className="grid grid-cols-2 gap-2 items-start">
          <div style={borderFor(flashKeys, 'startTime', true)} className="rounded-lg">
            <label className={labelCls}>Start time</label>
            <TimePicker value={config.startTime} onChange={(v) => updateConfig('startTime', v)} />
          </div>
          <div style={borderFor(flashKeys, 'endTime', true)} className="rounded-lg">
            <label className={labelCls}>End time</label>
            <TimePicker value={config.endTime} onChange={(v) => updateConfig('endTime', v)} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Mode</label>
          <div style={flashRing(flashKeys, 'mode')}>
            <Select
              size="sm"
              value={config.mode}
              onChange={(v) => updateConfig('mode', v)}
              options={MODES}
            />
          </div>
        </div>
        {field('venue', 'Venue', 'text', 'Registered office (default)')}
        {dateField('noticeDate', 'Notice date')}
      </Section>

      <Section title="People" missing={peopleMissing}>
        {field('chairperson', 'Chairperson')}
        {field('companySecretary', 'Company secretary')}
        {field('statutoryAuditor', 'Statutory auditor')}
        <div>
          <label className={labelCls}>Directors present</label>
          {directors.length === 0 ? (
            <div className="text-[11px] text-[#9a9080]">No directors on record.</div>
          ) : (
            <div className="space-y-1" style={borderFor(flashKeys, 'directorsPresent', true)}>
              {directors.map((d) => {
                const key = d.din || d.name;
                return (
                  <label key={key} className="flex items-center gap-2 text-[12px] text-[#1a2230]">
                    <input
                      type="checkbox"
                      checked={!!directorsChecked[key]}
                      onChange={(e) => setDirectorsChecked((prev) => ({ ...prev, [key]: e.target.checked }))}
                    />
                    <span className="truncate">{d.name}</span>
                    {d.din && <span className="text-[10px] text-[#9a9080]">DIN {d.din}</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <label className={labelCls}>Leave of absence (comma-separated)</label>
          <input
            className={inputCls}
            style={borderFor(flashKeys, 'absentDirectors', true)}
            value={absentDirectors.join(', ')}
            placeholder="Names granted leave"
            onChange={(e) => setAbsentDirectors(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
          />
        </div>
        <div>
          <label className={labelCls}>Invitees (comma-separated)</label>
          <input
            className={inputCls}
            style={borderFor(flashKeys, 'invitees', true)}
            value={invitees.join(', ')}
            placeholder="CS, auditor, advisors…"
            onChange={(e) => setInvitees(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
          />
        </div>
      </Section>

      <Section title="Agenda" missing={0}>
        <div className="space-y-1" style={borderFor(flashKeys, 'agenda', true)}>
          {presets.map((p) => (
            <label key={p.id} className="flex items-start gap-2 text-[12px] leading-snug text-[#1a2230]">
              <input
                type="checkbox"
                className="mt-[2px]"
                checked={!!agendaChecked[p.id]}
                onChange={(e) => setAgendaChecked((prev) => ({ ...prev, [p.id]: e.target.checked }))}
              />
              <span>{p.label}</span>
            </label>
          ))}
        </div>
        {customItems.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {customItems.map((t, i) => (
              <span key={`${t}-${i}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-[3px] rounded-full border" style={{ borderColor: '#c9dede', background: 'rgba(20,95,95,0.06)', color: '#145f5f' }}>
                {t}
                <button type="button" className="font-bold" onClick={() => setCustomItems((prev) => prev.filter((_, j) => j !== i))}>×</button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-1.5">
          <input
            className={inputCls}
            value={newAgenda}
            placeholder="Add a custom agenda item…"
            onChange={(e) => setNewAgenda(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newAgenda.trim()) {
                setCustomItems((prev) => [...prev, newAgenda.trim()]);
                setNewAgenda('');
              }
            }}
          />
          <button
            type="button"
            className="shrink-0 text-[11.5px] font-semibold px-2.5 rounded-lg text-white disabled:opacity-40"
            style={{ background: '#145f5f' }}
            disabled={!newAgenda.trim()}
            onClick={() => { setCustomItems((prev) => [...prev, newAgenda.trim()]); setNewAgenda(''); }}
          >
            Add
          </button>
        </div>
      </Section>

      <Section title="Business context" missing={0} defaultOpen={false}>
        {dateField('previousMeetingDate', 'Previous meeting date')}
        {field('previousMeetingNumber', 'Previous meeting no.')}
        {dateField('proposedAgmDate', 'Proposed AGM date')}
        {field('proposedAgmVenue', 'Proposed AGM venue')}
        {field('dividendRecommendation', 'Dividend recommendation')}
      </Section>
    </div>
  );
}
